import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

export { api, ytId, API, formatApiError } from "@/lib/api";

export const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/ad4a25d6-f9f8-4ebb-8af4-ecf657d6bc8a/images/d416c56491b0ef2d549bdd02d9c965c5d7411dcc1b11a1f6e4c4da43d7526464.jpeg";
export const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/ad4a25d6-f9f8-4ebb-8af4-ecf657d6bc8a/images/675015de15372907b7a53cd1f6c069a0642e0f87c83628b663ffd57a1650a9fb.jpeg";

export const Flourish = ({ className = "" }) => (
  <svg viewBox="0 0 120 12" className={className} fill="none" aria-hidden="true">
    <path d="M0 6 H48 M72 6 H120" stroke="currentColor" strokeWidth="1" />
    <path d="M60 1 L66 6 L60 11 L54 6 Z" stroke="currentColor" strokeWidth="1" />
    <circle cx="50" cy="6" r="1.4" fill="currentColor" />
    <circle cx="70" cy="6" r="1.4" fill="currentColor" />
  </svg>
);

export const Page = ({ children, className = "" }) => (
  <main className={`max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 ${className}`}>{children}</main>
);

export const PageHeader = ({ title, subtitle, crumbs = [], testid = "page-title" }) => (
  <div className="mb-10">
    <nav className="flex flex-wrap items-center gap-1 text-sm text-[#5c3a21] mb-4" data-testid="breadcrumbs">
      <Link to="/" className="hover:text-[#2a1508]">ہوم</Link>
      {crumbs.map((c, i) => (
        <span key={i} className="flex items-center gap-1">
          <ChevronLeft size={14} className="text-[#c5a059]" />
          {c.to ? (
            <Link to={c.to} className="hover:text-[#2a1508]">{c.label}</Link>
          ) : (
            <span className="text-[#2a1508] font-semibold">{c.label}</span>
          )}
        </span>
      ))}
    </nav>
    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2a1508] leading-[1.8]" data-testid={testid}>{title}</h1>
    {subtitle && <p className="mt-5 text-lg text-[#5c3a21] leading-[2.2]">{subtitle}</p>}
    <Flourish className="mt-4 w-44 text-[#c5a059]" />
  </div>
);

export const SectionCard = ({ to, icon: Icon, title, desc, testid, accent = "#c5a059", tint = "#f5efe3" }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group relative overflow-hidden rounded-2xl p-6 text-center transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_18px_45px_rgba(42,21,8,0.22)] border border-[#d4af37]/40 hover:border-[#d4af37] bg-[#fdfbf7]"
    style={{ boxShadow: "0 4px 18px rgba(42,21,8,0.08), inset 0 1px 0 rgba(255,255,255,0.6)" }}
  >
    <div className="absolute inset-0 opacity-[0.55] transition-opacity duration-300 group-hover:opacity-80" style={{ background: `radial-gradient(circle at 50% -20%, ${tint} 0%, transparent 65%)` }} />
    <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
    {Icon && (
      <div
        className="relative mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center text-white shadow-[0_6px_16px_rgba(42,21,8,0.25)] transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3"
        style={{ background: `linear-gradient(135deg, ${accent}, #2a1508)` }}
      >
        <Icon size={26} strokeWidth={1.6} />
      </div>
    )}
    <h3 className="relative text-xl sm:text-2xl font-bold text-[#2a1508] leading-[2]">{title}</h3>
    {desc && <p className="relative text-sm text-[#5c3a21] mt-2 leading-[2]">{desc}</p>}
    <span className="relative mt-4 inline-block w-10 h-0.5 rounded-full transition-all duration-300 group-hover:w-16" style={{ backgroundColor: accent }} />
  </Link>
);

export const Medallion = ({ name = "", className = "" }) => (
  <div className={`rounded-full border-2 border-[#d4af37] bg-[#f5efe3] text-[#2a1508] flex items-center justify-center font-bold shadow-[inset_0_2px_8px_rgba(42,21,8,0.1)] ${className}`}>
    <span className="leading-none pt-2">{name.trim().charAt(0)}</span>
  </div>
);

export const RichText = ({ text = "", className = "" }) => (
  <div className={`space-y-5 text-right ${className}`}>
    {text.split(/\n{2,}/).filter(Boolean).map((p, i) =>
      p.startsWith("## ") ? (
        <h3 key={i} className="text-xl sm:text-2xl font-bold text-[#2a1508] pt-5 leading-[2]">{p.slice(3)}</h3>
      ) : (
        <p key={i} className="text-lg leading-[2.3] text-[#33200f] whitespace-pre-line">{p}</p>
      )
    )}
  </div>
);

export const Loading = ({ label = "لوڈ ہو رہا ہے…" }) => (
  <div className="py-24 text-center text-[#5c3a21] text-lg animate-pulse" data-testid="loading-indicator">{label}</div>
);

export const ErrorNote = ({ message = "مواد نہیں مل سکا" }) => (
  <div className="py-16 text-center" data-testid="error-note">
    <p className="text-xl text-[#5c3a21]">{message}</p>
    <Link to="/" className="inline-block mt-6 px-6 py-2 rounded-full bg-[#2a1508] text-[#f5efe3] hover:bg-[#4a2b16] transition-colors" data-testid="error-home-link">صفحۂ اول</Link>
  </div>
);
